import { useEffect, useRef, useState, type ReactNode } from 'react';
import { getCheckoutToken, setCheckoutToken } from '../lib/api';
import { useAuthOptional } from '../lib/auth-context';
import { useLocale, useT } from '../i18n/locale-context';
import { EMAIL_SYNTAX_KEY, isLikelyEmail, resolveAuthError } from '../lib/auth-error';
import { emailDomainShape, suggestRefusedEmail } from '../lib/email-domain-hint';
import { INPUT_CLASS } from '../lib/form-styles';
import { centreInScroller } from '../lib/use-visual-viewport-sheet-height';
import { OtpResendButton } from './otp-resend-button';
import { track } from '../lib/funnel';

/**
 * How long before «Отправить код ещё раз» is offered.
 *
 * 45s rather than `OtpResendButton`'s 60s default: this is a PAYMENT sheet, not
 * a login screen. The buyer is holding a card and the alternative to waiting is
 * abandoning a purchase, so the rescue is worth reaching a quarter of a minute
 * sooner. It is also the same 45s the card modal already uses for the Payme
 * SMS, which is the other resend a buyer can meet inside this same flow.
 */
const RESEND_COOLDOWN_S = 45;

/**
 * The address, as ONE FIELD on the payment sheet the buyer is already looking at.
 *
 * ## What shape this is, and why it is this one
 *
 * MR !565 put the identification in its OWN sheet (`checkout-identify-modal`):
 * a guest tapped «Оформить», answered «кто вы», that sheet closed, and the card
 * sheet opened. It measured better than the `navigate('/login')` it replaced —
 * 245 guests a month hit that wall and 0 of them reached checkout — but Igor
 * rejected it the same day (2026-09-19): «я думал там просто поле почты и поле
 * ввода карты, как обычно делают — оплатил, и мы сразу создаём аккаунт. А
 * сейчас это по сути обычная рега через модалку.»
 *
 * The second attempt (`feat/guest-checkout-one-sheet`) put the field on the card
 * sheet but built the guest a DIFFERENT sheet: no Payme/Octobank method strip,
 * and a Telegram button behind an «или» divider wedged between the address and
 * the card. Igor rejected that too, for two reasons worth writing down because
 * they are the whole specification of this file:
 *
 *   «нельзя выбрать метод оплаты, это просто эмуляция оплаты» — the strip is
 *   not decoration, it is the evidence that a real gateway is on the other end.
 *   Without it the sheet reads as a form that pretends to take money.
 *
 *   The Telegram option + «или» turned the top of a payment sheet back into a
 *   registration screen — exactly the thing the one-sheet was meant to stop
 *   looking like.
 *
 * So the rule this file exists to hold: **the guest sees the authed sheet,
 * unchanged, with exactly one extra field — email — above the payment method.
 * Nothing else added, nothing removed.** No Telegram here, no divider, no
 * bordered "who you are" panel, no second sheet. Telegram is still offered at
 * first-login onboarding, where it is a second key rather than a fourth choice
 * on a checkout.
 *
 * ## The contract
 *
 *  - `ensure()` resolves TRUE only when the ACCOUNT exists. Everything the
 *    caller does after it — tokenize, charge, redirect, issue an invoice — may
 *    assume `payment_orders.user_id` / `invoices.user_id` have a user to point
 *    at, which are NOT NULL and stay so.
 *  - `commit()` is the other half, and it runs only once the money has moved.
 *    `ensure()` buys the ACCOUNT; `commit()` gives this BROWSER the session.
 *  - `/auth/start` answers three ways and all three are answered HERE.
 *    `signed_in` (a brand-new address, or a returning EMPTY soft account) is
 *    asked for SILENTLY: the server creates the account, sets no cookie and
 *    mails nothing, and hands back a token this hook keeps in memory for
 *    `/billing/*`. `ensure()` returns true immediately, so the caller's submit
 *    runs straight on into the payment. `code_required` / `password_required`
 *    reveal that ONE field directly under the email and return false; the buyer
 *    fills it and presses the SAME primary button again, which re-enters
 *    `ensure()` at the new stage. Those two DO end in a real session — the
 *    address belongs to an account with something to protect, and proving it is
 *    the whole point of the step. Everything else on the sheet — the card, the
 *    ИНН — is the caller's own React state and nothing unmounts, so nothing
 *    typed is lost.
 *  - **The browser stays a guest until the payment succeeds** (Igor,
 *    2026-09-20: «гость до подтверждения оплаты»). Before this, `/auth/start`
 *    set the session cookie on «Оплатить»: the app flipped to signed-in,
 *    onboarding popped over the sheet and the email froze — all before the card
 *    was charged, so a REFUSED card left the buyer signed into an address he
 *    may have mistyped, with no way to correct it. Now nothing about this
 *    browser changes until `commit()`.
 *  - No confirmation stop for a brand-new address (Igor, 2026-09-18: «почту
 *    ввели и сразу карту»). A typo cannot block the purchase — the session is on
 *    this device — and first-login onboarding still offers Telegram afterwards.
 *  - No PHONE. There is no phone login, so a phone-only account could never be
 *    recovered after a cleared cookie jar and the buyer would be locked out of a
 *    subscription they had paid for. The phone is still collected after payment.
 */
export interface GuestIdentify {
  /**
   * This browser is still a guest — render `node` and await `ensure()` before
   * charging.
   *
   * ⚠️ Stays TRUE across a silent identification, unlike the `identified` flag
   * below. It falls to false only when a REAL session exists, which is
   * `commit()` (or the code/password stage, which signs the buyer in itself).
   * That is what keeps the email field on the sheet and editable while the card
   * is being typed.
   */
  active: boolean;
  /** This checkout has an owner: a checkout token is held, or a real session
   *  was established during this hook's life. */
  identified: boolean;
  /**
   * Enough is typed to be worth trying. Callers whose buttons are DISABLED until
   * a payment can succeed (the one-time gateway chooser, whose buttons leave the
   * site) gate on this. The card sheet deliberately does NOT: a disabled button
   * fires no DOM events, and that is exactly how 128 `checkout_failed`
   * `{empty_number}` taps stayed invisible for a month.
   */
  ready: boolean;
  /** The address as typed — the sheets key their identify debounce on it, so
   *  the 600 ms restart on every keystroke and not only on the first one that
   *  makes the address well-formed (see the return statement below). */
  email: string;
  /** A round trip is in flight — for callers whose own button has no spinner. */
  busy: boolean;
  /**
   * The client-side address check, on its own. Lets a caller surface a typo in
   * the SAME tap as its own field errors instead of one error per tap. Safe to
   * call twice: `ensure()` runs it again.
   */
  precheck: () => boolean;
  /** Take the next identification step. True ⇔ the account now exists. */
  ensure: () => Promise<boolean>;
  /**
   * The money has moved — turn the checkout token into a real session.
   *
   * Call it after a charge has been ACCEPTED (the card path) or immediately
   * before handing the buyer to a hosted provider page they are about to land
   * on (Octobank, one-time Payme/Click, a bank invoice). A no-op when no token
   * is held — a signed-in buyer, or a guest who proved a code/password and so
   * already has the real thing.
   *
   * ⚠️ Never rejects. It runs AFTER a successful payment, inside the caller's
   * own try/catch, and a failed adopt must not be reported to the buyer as a
   * failed purchase. The cost of a failure is that this browser stays a guest
   * until the next sign-in — the payment, the order and the account are all
   * safely server-side.
   */
  commit: () => Promise<void>;
  /**
   * Drop the checkout token without using it — for a host that tears its sheet
   * down. Also runs on unmount and when `enabled` goes false, so a token can
   * never leak into another sheet, another plan card or another page.
   */
  reset: () => void;
  /** The email field, plus whichever single credential field was revealed. */
  node: ReactNode;
}

/**
 * Bring a focused field into the visible part of a sheet whose bottom half the
 * software keyboard has just covered. The card sheet does this for every field
 * via `useVisualViewportSheetHeight`; the Radix gateway sheet does not, and on
 * 390×844 the email input sits exactly where the keyboard lands. Deferred past
 * the keyboard's ~300ms slide, and feature-detected (jsdom has no layout).
 *
 * ⚠️ Scroll the SHEET'S OWN SCROLLER, never `scrollIntoView`, wherever one
 * exists (2026-09-20). `scrollIntoView` scrolls EVERY scrollable ancestor, the
 * document included — and on iOS `body{overflow:hidden}` does not hold the page
 * (that is the whole reason `<RemoveScroll>` wraps these sheets). Panning the
 * layout viewport under a `position:fixed` overlay is the hazard
 * `use-visual-viewport-sheet-height.ts` documents at length («дёргает и я опять
 * вверху модалки»), and while the overlay is panned iOS PAINTS the sheet in one
 * place and HIT-TESTS it in another: the next tap lands on nothing. Since the
 * address block moved directly on top of the card number, the next tap is
 * «Номер карты» — which stopped focusing and stopped opening the keyboard while
 * the email beside it kept working (Igor, iPhone, 2026-09-20). Chromium cannot
 * reproduce it: it honours `body{overflow:hidden}`, so there is nothing to pan.
 *
 * The `scrollIntoView` fallback is kept for the ONE host with no scroller of
 * its own — the «Оплатить разово» gateway sheet, a `max-h-[92dvh]` Radix box
 * with no `overflow-y-auto` body — where doing nothing would leave the field
 * under the keyboard. Nothing is tapped under that field there: it is the last
 * control above the gateway buttons.
 */
let scrollFocusedTimer: number | null = null;
function scrollFocusedIntoView(el: HTMLElement): void {
  if (typeof window === 'undefined') return;
  // One pending scroll at a time, and not `smooth`: a field focused within the
  // 320 ms window used to be scrolled AFTER the buyer had already moved on to
  // the next one, and a smooth scroll is cancelled by the next touch — the sheet
  // panned back to the email while the card number was being typed.
  if (scrollFocusedTimer !== null) window.clearTimeout(scrollFocusedTimer);
  scrollFocusedTimer = window.setTimeout(() => {
    scrollFocusedTimer = null;
    if (document.activeElement !== el) return;
    if (centreInScroller(el)) return;
    // No scroller at all — the gateway sheet. Its own box does not scroll, so
    // there is nothing safer to move than the page.
    if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
  }, 320);
}

export function useGuestIdentify({
  enabled,
  onIdentified,
  onEnter,
  inputClassName = INPUT_CLASS,
  inputErrorClassName,
  labelClassName = 'mb-1.5 block text-[12.5px] font-semibold text-ink',
}: {
  /** False for a signed-in buyer — then `node` is null, `active` false and
   *  `ensure()` resolves true without a request. Nothing about the authed
   *  checkout changes. */
  enabled: boolean;
  onIdentified?: () => void;
  /**
   * ENTER in the address field, once the address has PASSED `precheck()`.
   *
   * Enter there used to do nothing at all (Igor, 2026-09-20). It could not
   * submit — the block sits outside the card `<form>` on purpose, so that a
   * stray Enter cannot fire a card validation against an empty card — and
   * nothing else was listening, so the one key a buyer presses after typing an
   * address was a dead key on a payment sheet.
   *
   * The host decides where "on" is, because only the host knows which body is
   * on screen: the card number on the Payme sheet, «Продолжить на Octobank» on
   * the Octobank one. It MOVES focus and never presses anything — Enter in an
   * email field must not start a charge or leave the site.
   *
   * A malformed address does not call this; `precheck()` has already marked the
   * field and put the caret back in it, exactly as the primary button does.
   *
   * ⚠️ Optional, and the handler is only attached when it is given. On the
   * invoice sheet this block is INSIDE the host's `<form>`, where Enter already
   * submits — swallowing it there would take a working key away.
   */
  onEnter?: () => void;
  /** The HOST sheet's own input class, so the extra field is indistinguishable
   *  from the fields beside it. Igor's rule is "the same sheet plus one field",
   *  and a field in a different skin is a different sheet. */
  inputClassName?: string;
  /**
   * The host sheet's own MARKED-field class. A press that cannot go through
   * lights every missing field at once, and this one has to look like the card
   * number beside it — a message with no red border reads as a hint, not as the
   * thing blocking the button. Swapped for `inputClassName`, never appended:
   * Tailwind cannot resolve two border colours by class order.
   */
  inputErrorClassName?: string;
  /** The host sheet's own label class, same reason. */
  labelClassName?: string;
}): GuestIdentify {
  const t = useT();
  const { locale } = useLocale();
  /*
   * OPTIONAL on purpose. This hook is mounted by /plans, which does have an
   * AuthProvider — but `useAuthOptional` keeps it renderable in any unit test
   * that mounts a host component without one, and a hook that throws at mount
   * would take the whole checkout down for a signed-in buyer too.
   */
  const auth = useAuthOptional();

  const [email, setEmail] = useState('');
  const [stage, setStage] = useState<'email' | 'code' | 'password'>('email');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  /**
   * The corrected address we are offering, or null.
   *
   * Mutually exclusive with `error` — the owner's rule is one message per
   * field, and these two ARE the field's one message. Every write goes through
   * `showError` / `showSuggestion` so the pair cannot drift.
   */
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [identified, setIdentified] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  /** The block itself — what gets centred, so the message UNDER the field
   *  lands on screen with it and not just above the keyboard's edge. */
  const blockRef = useRef<HTMLDivElement>(null);
  /** Consecutive address rejections, reset only by one that passes. Drives the
   *  «Например: …» that appears from the second failure on. A ref, not state:
   *  it never renders on its own, and a re-render per failed tap would be a
   *  re-render per tap in the 24-tap case this exists for. */
  const emailFails = useRef(0);

  /** The field's ONE message is a sentence… */
  function showError(message: string): void {
    setSuggestion(null);
    setError(message);
  }

  /** …or an offer. Never both. */
  function showSuggestion(next: string): void {
    setError(null);
    setSuggestion(next);
  }

  /** …or nothing at all. */
  function clearMessage(): void {
    setError(null);
    setSuggestion(null);
  }

  /**
   * Put the caret in a rejected field AND bring it — with its message — back
   * onto the visible part of the sheet.
   *
   * ⚠️ `preventScroll` + `centreInScroller`, the rule every focus move in these
   * sheets follows: letting the browser scroll a focused element also lets iOS
   * pan the layout viewport under a `position:fixed` overlay, which paints the
   * sheet in one place and hit-tests it in another. Never `scrollIntoView`
   * while a scroller exists — the fallback below is for the ONE host that has
   * none (the «Оплатить разово» gateway sheet), where doing nothing would leave
   * the field under the keyboard, which is the bug being fixed.
   *
   * The BLOCK is centred rather than the input: the message renders under the
   * field, and on 360×365 with the keyboard up (Instagram's in-app browser,
   * 2026-09-20) a centred INPUT still leaves its message at the fold. Centring
   * runs before React has painted the new message, which is fine and
   * deliberate — a centred block has half the scroller below it, and the
   * message is one line.
   */
  function revealField(el: HTMLInputElement | null): void {
    if (!el) return;
    el.focus({ preventScroll: true });
    const target: HTMLElement = el === emailRef.current ? (blockRef.current ?? el) : el;
    if (centreInScroller(target)) return;
    if (typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'center' });
  }

  /*
   * STICKY. `enabled` is derived from the live auth status, and this checkout
   * can flip that status halfway through the buyer's work — so a straight read
   * would pull the email field (and its revealed code field, and the error line)
   * out of the sheet at the exact moment it succeeded. Latched on, never off:
   * once this checkout belongs to someone who arrived without an account, it
   * belongs to them for the rest of the sheet's life.
   *
   * Since 2026-09-20 a silent `/auth/start` no longer flips anything, so the
   * moment this guards is the code/password stage (`verifyOtp`/`login`, a real
   * sign-in) and `commit()` after a successful payment.
   *
   * It cannot latch by accident on a signed-in buyer: the caller passes
   * `authKnown && !isAuthed`, which is false while auth is still resolving.
   */
  const everEnabled = useRef(false);
  if (enabled) everEnabled.current = true;
  const isGuest = everEnabled.current;

  /*
   * THE CHECKOUT TOKEN THIS HOOK HOLDS.
   *
   * A ref, not state: the module-level store in `api.ts` is the thing requests
   * actually read, and this is only a record of whether the token there is
   * OURS. /plans mounts THREE of these hooks at once (card, one-time, invoice),
   * so a hook that cleared the store blindly would drop a sibling's token; every
   * clear below is conditional on the store still holding this instance's own.
   */
  const heldToken = useRef<string | null>(null);

  /** Give up this hook's token, if the store still holds it. */
  function dropToken(): void {
    const mine = heldToken.current;
    heldToken.current = null;
    if (mine !== null && getCheckoutToken() === mine) setCheckoutToken(null);
  }

  /*
   * A guest until the session is REAL.
   *
   * This used to be `isGuest && !identified`, which is why the email vanished
   * the moment «Оплатить» was pressed — identification and sign-in were the
   * same event. They are not any more: a silent `/auth/start` leaves
   * `auth.status` at `guest` and the field must stay on the sheet, editable,
   * for as long as that is true. `commit()` (or a code/password sign-in) is
   * what ends it.
   */
  const active = isGuest && auth?.status !== 'authed';

  /*
   * Let go of the token when this sheet goes away — an unmount, or `enabled`
   * falling because the buyer became a member some other way. Without it a
   * token minted on /plans would still be attached to `/billing/*` on the next
   * page the buyer opens.
   */
  useEffect(() => {
    if (!enabled) dropToken();
    // `dropToken` reads only refs and module state, so it is stable in effect —
    // deliberately out of the dep list, which would otherwise re-run this on
    // every render. (No `react-hooks` plugin in this repo's eslint config.)
  }, [enabled]);
  useEffect(
    () => () => {
      dropToken();
    },
    [],
  );

  /*
   * Is the ONE field this block is currently showing fit to send? Marks it and
   * puts the caret in it when it is not.
   *
   * Every primary button that can be blocked by an empty field calls this on
   * PRESS rather than disabling itself (Igor, 2026-09-19: «кнопка жмётся пусть,
   * но подсвечивает, что не заполнено»). A disabled button fires no DOM events,
   * which is how 128 `checkout_failed{empty_number}` taps stayed invisible for a
   * month, and it never says WHY it is dead.
   *
   * ⚠️ Only the EMAIL stage emits. «ivan@gmail» passes `<input type="email">`
   * (the HTML spec says `a@b` is valid) and fails the API's `IsEmail`, so a
   * client-side block there is a rejected attempt exactly as a server one is and
   * has always been recorded — same contract as login.tsx. An empty code or
   * password box is not a rejected attempt, it is an unfinished form, and
   * inventing a row for it would inflate the only auth-failure series we have.
   */
  function precheck(candidate: string = email): boolean {
    if (!active) return true;
    if (stage === 'code') {
      if (code.replace(/\D/g, '').length === 6) return true;
      showError(t('register.codeIncomplete'));
      revealField(codeRef.current);
      return false;
    }
    if (stage === 'password') {
      if (password.length > 0) return true;
      showError(t('auth.error.passwordRequired'));
      revealField(passwordRef.current);
      return false;
    }
    if (isLikelyEmail(candidate)) {
      emailFails.current = 0;
      return true;
    }
    emailFails.current += 1;
    /*
     * `domainShape` — WHAT was wrong, never WHOSE address it was. Never the
     * local part, never the raw domain: `typo:gmail.com` names a constant of
     * ours, not anything typed. Without it this row said only "a client-side
     * address check refused something", which cannot tell a missing dot (fixable
     * with a suggestion) from a missing `@` (fixable only by typing again).
     */
    track('login_failed', {
      method: 'start',
      reason: 'validation_error',
      client: true,
      domainShape: emailDomainShape(candidate),
    });
    /*
     * ONE message. A suggestion REPLACES the sentence about the rule — telling
     * someone their address is malformed AND offering the fixed one puts the
     * answer underneath the complaint, and on a 360×365 viewport the complaint
     * is all that fits.
     */
    const fixed = suggestRefusedEmail(candidate);
    if (fixed) showSuggestion(fixed);
    /*
     * The example arrives on the SECOND consecutive failure, not the first: it
     * is four extra words, and someone who mistyped once and can see the field
     * does not need to be shown what an e-mail looks like. Someone who has now
     * failed twice does — that is the 24-taps case, where the rule was restated
     * verbatim each time and never became more useful.
     *
     * "Consecutive" deliberately survives an EDIT: the counter is reset only by
     * an address that passes. The buyer in that session kept correcting and
     * kept being wrong, which is exactly the run this is counting.
     */ else showError(withExample(t(EMAIL_SYNTAX_KEY)));
    revealField(emailRef.current);
    return false;
  }

  /** The example, appended to the message rather than added beside it — the
   *  owner's rule is one message per field. */
  function withExample(message: string): string {
    return emailFails.current >= 2 ? `${message} ${t('checkout.identify.emailExample')}` : message;
  }

  /**
   * Take the suggestion: the address becomes the corrected one and the check
   * runs again on THAT value.
   *
   * ⚠️ `precheck(next)` takes the new address as an argument rather than
   * reading state — `setEmail` has not committed by the time this line runs, so
   * a re-check against `email` would re-reject the very value it just fixed and
   * the line would look broken.
   */
  function applySuggestion(): void {
    const next = suggestion;
    if (!next) return;
    setEmail(next);
    setSuggestion(null);
    setError(null);
    // A NEW ADDRESS IS A NEW CHECKOUT — same rule as typing one, see `onChange`.
    if (identified) {
      setIdentified(false);
      dropToken();
    }
    precheck(next);
  }

  /** Mark this block without submitting — for a host whose OWN fields are also
   *  invalid and which wants every one of them lit on the same press. Returns
   *  true when there is nothing to complain about.
   *
   *  Wrapped rather than exported directly: a caller that passed it straight to
   *  an `onClick` would hand `precheck` a DOM event as its address. */
  const markIfIncomplete = (): boolean => precheck();

  async function ensure(): Promise<boolean> {
    if (!active) return true;
    /*
     * ALREADY ANSWERED. `active` no longer falls when the account is created,
     * so this is what stops a second press — a retry after a refused card, a
     * gateway swap, a second one-time attempt — from minting a second account
     * or a second token for the same address. Dropped again the moment the
     * buyer edits the email, because a token belongs to the address it was
     * minted for.
     */
    if (identified) {
      /* Re-assert ours if a sibling sheet's cleanup took the single slot in
         `api.ts` — three of these hooks are mounted at once on /plans. */
      if (heldToken.current !== null && getCheckoutToken() !== heldToken.current) {
        setCheckoutToken(heldToken.current);
      }
      return true;
    }
    if (!auth) return false;
    if (!precheck()) return false;
    clearMessage();
    setBusy(true);
    try {
      if (stage === 'password') {
        await auth.login({ email: email.trim(), password });
      } else if (stage === 'code') {
        await auth.verifyOtp({ email: email.trim(), code: code.trim(), locale });
      } else {
        /*
         * SILENT. The account is created and the order gets its owner, but the
         * server sets no cookie and mails no code, and hands the session back
         * in the body instead. The browser holds it in memory for `/billing/*`
         * and stays, to every other part of the app, a guest.
         */
        const result = await auth.start({ email: email.trim(), locale, silent: true });
        if (result.status === 'password_required') {
          setStage('password');
          return false;
        }
        if (result.status === 'code_required') {
          // `/auth/start` has ALREADY mailed the code server-side. Do NOT call
          // requestOtp here — a re-request invalidates the first one (login.tsx).
          setStage('code');
          return false;
        }
        /*
         * `accessToken` is absent only against an API that predates `silent`
         * (the web and the API ship on separate pipelines — see the note on
         * `Plan.priceUzsOneTime`). That server set the cookie itself, so the
         * payment still authenticates; `commit()` then has nothing to do and
         * the browser catches up with the session on its next `/auth/me`.
         */
        heldToken.current = result.accessToken ?? null;
        // Only ever WRITE the shared slot — a `null` here would drop whatever a
        // sibling sheet is holding, and in that branch we have nothing to put
        // in its place anyway.
        if (heldToken.current !== null) setCheckoutToken(heldToken.current);
      }
      setIdentified(true);
      onIdentified?.();
      return true;
    } catch (err) {
      showError(resolveAuthError(err, t));
      return false;
    } finally {
      setBusy(false);
    }
  }

  /**
   * Re-mail the login code.
   *
   * ⚠️ Fires NOTHING, deliberately. `requestOtp` emits only on FAILURE
   * (`authFailure('login_failed', 'otp_request', …)` inside the auth context),
   * which is exactly what the sheet this replaces did. `otp_requested` in the
   * registry belongs to the Payme SMS in `card-save-modal` and must not be
   * borrowed for the email code: the two are different codes on different rails,
   * and merging them would make the card flow's own request/submit gap
   * unreadable.
   *
   * The `catch` is the one thing the old wiring did NOT do. `requestOtp` throws
   * on failure and `OtpResendButton` does not catch, so a failed resend used to
   * surface as an unhandled rejection and nothing on screen.
   */
  async function resendCode(): Promise<void> {
    if (!auth) return;
    clearMessage();
    try {
      await auth.requestOtp({ email: email.trim(), locale });
    } catch (err) {
      showError(resolveAuthError(err, t));
    }
  }

  /**
   * The payment went through — take the session.
   *
   * See the note on `GuestIdentify.commit`: this runs after the money has
   * moved, so it swallows its own failure rather than letting a paid buyer be
   * told the purchase failed. The token is kept on a failure (the context only
   * drops it on success), so the rest of this page still authenticates.
   */
  async function commit(): Promise<void> {
    if (!auth || heldToken.current === null) return;
    /* Never adopt a token that is not ours: the store holds one slot and
       another sheet on this page may have taken it. */
    if (getCheckoutToken() !== heldToken.current) return;
    try {
      await auth.adoptCheckoutSession();
      heldToken.current = null;
    } catch {
      /* The purchase is safely server-side; this browser stays a guest. */
    }
  }

  function backToEmail(): void {
    setStage('email');
    setCode('');
    setPassword('');
    clearMessage();
  }

  const ready =
    !active ||
    (stage === 'email'
      ? isLikelyEmail(email)
      : stage === 'code'
        ? code.replace(/\D/g, '').length === 6
        : password.length > 0);

  /** The class for a field on the CURRENT stage, marked or not. */
  const fieldClass = (marked: boolean) =>
    marked && inputErrorClassName ? inputErrorClassName : inputClassName;
  /* A SUGGESTION marks the field exactly as a message does. The two are one
     message (see `showError` / `showSuggestion`), and an offer with no red
     border reads as a hint rather than as the thing blocking the button. */
  const marked = !!error || !!suggestion;

  /*
   * The way back from a stage that FROZE the address.
   *
   * Only the code and password stages need it — those are the ones where a
   * session is really being established and the field is locked. The silent
   * path has no use for it: the input itself is editable there.
   *
   * A LINK, not muted ink (Igor, 2026-09-20: «заметнее»). At 12px in
   * `text-ink-muted` it carried exactly the weight of «Код отправлен на …»
   * directly above it, so the one control that rescues a mistyped address read
   * as another line of explanation and was scrolled past.
   */
  const changeEmail = (
    <button
      type="button"
      onClick={backToEmail}
      className="mt-2 inline-block text-[12.5px] font-semibold text-brand underline underline-offset-2 transition-colors hover:text-brand-mid"
    >
      {t('register.changeEmail')}
    </button>
  );

  /*
   * NO frame. The reverted attempt grouped these fields in a bordered bone panel
   * because a Telegram button and an «или» divider sat inside them and needed
   * corralling. With those gone there is nothing to corral: this is one labelled
   * field in the host sheet's own field style, sitting in the host sheet's own
   * flow — which is the entire brief.
   */
  const node = !isGuest ? null : (
    <div className="space-y-3" ref={blockRef} data-testid="guest-identify">
      <div>
        <label htmlFor="checkout-email" className={labelClassName}>
          {t('login.email')}
        </label>
        <input
          id="checkout-email"
          name="email"
          type="email"
          autoComplete="username"
          inputMode="email"
          ref={emailRef}
          value={email}
          /* EDITABLE for as long as this browser is a guest (Igor, 2026-09-20).
             A silent identification settles nothing a buyer can see: the card
             can still be refused, and a mistyped address is one of the reasons
             it might be. Freezing the field there is what left someone locked
             into an account they never meant to create.
             The code/password stages still freeze it, unchanged: a code has
             been mailed TO this address and editing it silently would answer
             for a different one. «Указать другой адрес» is the way back.

             ⚠️ NOT disabled once the session has been taken (`!active`), only
             inert. `disabled` drops the focus, and a sheet lifted above the
             keyboard comes down when its field loses the focus (see
             `use-visual-viewport-sheet-height`). Since 2026-10-01 `commit()`
             runs the moment the pay LINK is ready — while the buyer is reaching
             for it — so the sheet must not be given a reason to move then.
             Read from the code, not seen in a recording: the lost tap of
             2026-10-02 (touch on «Продолжить на Octobank», click on the sheet
             body) was the tap's OWN blur dropping the lift, fixed in that hook
             (`BLUR_LIFT_GRACE_MS`); this closes the neighbouring way to the
             same jump. */
          disabled={stage !== 'email'}
          aria-readonly={!active || undefined}
          onChange={(e) => {
            // The address has been answered and adopted: the box is only kept
            // so that nothing moves (see above). Typing into it changes nothing.
            if (!active) return;
            setEmail(e.target.value);
            /* The message — sentence or offer — goes out the moment it stops
               describing what is in the box. `emailFails` deliberately does
               NOT: a run of failures is what the «Например: …» counts, and
               editing between two wrong addresses is part of the run. */
            if (error || suggestion) clearMessage();
            /* A NEW ADDRESS IS A NEW CHECKOUT. The token belongs to the address
               it was minted for, so an edit gives it up and the next press runs
               `ensure()` again — a new address mints a new soft account, the
               same address silently returns the same empty one (the API's
               `startAuth` rule, 2026-09-20). */
            if (identified) {
              setIdentified(false);
              dropToken();
            }
          }}
          onFocus={(e) => scrollFocusedIntoView(e.currentTarget)}
          onKeyDown={
            onEnter
              ? (e) => {
                  if (e.key !== 'Enter') return;
                  /* Never let it reach a form. On the card sheet there is no
                     enclosing form to reach (that is the point of the block's
                     position); on any host that DOES enclose it, an Enter in
                     the address must still not submit the rest. */
                  e.preventDefault();
                  if (precheck()) onEnter();
                }
              : undefined
          }
          aria-invalid={marked && stage === 'email'}
          className={`${fieldClass(marked && stage === 'email')} disabled:opacity-70 ${active ? '' : 'opacity-70'}`}
        />
        {stage === 'email' && (
          <p className="mt-1 text-[11.5px] leading-snug text-ink-muted">
            {t('checkout.identify.emailHint')}
          </p>
        )}
      </div>

      {active && stage === 'code' && (
        <div>
          <label htmlFor="checkout-email-code" className={labelClassName}>
            {t('register.code')}
          </label>
          <input
            id="checkout-email-code"
            ref={codeRef}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onFocus={(e) => scrollFocusedIntoView(e.currentTarget)}
            aria-invalid={marked}
            className={fieldClass(marked)}
          />
          <p className="mt-1 text-[11.5px] leading-snug text-ink-muted">
            {t('register.codeSentTo')} {email.trim()}
          </p>
          {/* A code that never arrives must not be a dead end — this field is
              the whole checkout now, so "wait or give up" means "give up". The
              SHARED component, not a local link: it already carries the
              cooldown, the «не пришло письмо?» framing and the three strings in
              all locales, and its own measurement (237/237 who re-requested went
              on to confirm, TZ 2026-08-28) is why a resend is presented as a
              rescue rather than a footnote. */}
          <div className="mt-3">
            <OtpResendButton onResend={resendCode} cooldownSeconds={RESEND_COOLDOWN_S} />
          </div>
          {changeEmail}
        </div>
      )}

      {active && stage === 'password' && (
        <div>
          <label htmlFor="checkout-email-password" className={labelClassName}>
            {t('login.password')}
          </label>
          <input
            id="checkout-email-password"
            ref={passwordRef}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={(e) => scrollFocusedIntoView(e.currentTarget)}
            aria-invalid={marked}
            className={fieldClass(marked)}
          />
          {changeEmail}
        </div>
      )}

      {/*
       * THE FIELD'S ONE MESSAGE — an offer when we can tell what was meant, a
       * sentence otherwise, never both (see `showError` / `showSuggestion`).
       *
       * The offer is a BUTTON, not a line of prose with the address in it: the
       * whole point is that accepting it costs one tap on a phone whose
       * keyboard is already covering half the sheet. `type="button"` because
       * the invoice sheet encloses this block in its own `<form>` — a default
       * submit here would issue a счёт instead of fixing the address.
       */}
      {suggestion && (
        <p className="text-[12px] leading-snug" role="alert">
          <button
            type="button"
            onClick={applySuggestion}
            className="text-left font-semibold text-brand underline underline-offset-2 transition-colors hover:text-brand-mid"
          >
            {t('checkout.identify.didYouMean', { email: suggestion })}
          </button>
        </p>
      )}

      {error && (
        <p className="text-[12px] font-medium leading-snug text-status-closing-text" role="alert">
          {error}
        </p>
      )}
    </div>
  );

  return {
    active,
    identified,
    ready,
    /**
     * The address as typed, for the sheets' identify debounce to key on.
     *
     * `ready` alone is not enough: it flips to true on the first keystroke that
     * makes the address well-formed and stays true, so a timer keyed on it
     * never restarts. `ivan@gmail.co` is well-formed; the `m` that follows did
     * not re-arm the timer, and `ensure()` from that earlier render created the
     * account for the address minus its last letter (prod, 2026-09-22). Keying
     * the timer on the string itself makes «600 ms after the LAST keystroke»
     * literally true, and the closure that fires is the one that saw it.
     */
    email,
    busy,
    precheck: markIfIncomplete,
    ensure,
    commit,
    reset: dropToken,
    node,
  };
}
